/**
 * IntakeLinkSendBar — staff modal to mint + send a client intake-form link
 * (M5-S2, AD-020/AD-022).
 *
 * Flow:
 *   1. On mount: load clients/{cid} (name, whatsapp, email); detect channels
 *      (AD-020 — WA-first, email fallback, both if both, error if neither).
 *      Generate the link up-front via generateIntakeLink (service-selectable,
 *      default L1A) so the URL exists before send.
 *   2. Staff sees the client name, a green WhatsApp pill (if a number is on
 *      file) and a blue Email pill (if an email is on file), each toggleable.
 *      If NEITHER is present → a red "no contact channel" notice; send disabled.
 *   3. Confirm Send → calls each selected send CF; on success the CF writes an
 *      internal (clientVisible:false) project-thread note. We close.
 *
 * Tokens only — var(--accent)/var(--accent-wash) for WA, var(--info)/
 * var(--info-wash) for email. Reuses the locked modal scaffold (ovl/sheet/head)
 * and Btn3 — no new colour primitives, no hardcoded hex.
 */
import { useEffect, useState } from 'react';
import { Btn3 } from './ui';
import { useToast } from './Toast';
import { useAuth } from '../lib/auth';
import PropertyPicker from './PropertyPicker';
import {
  INTAKE_SERVICES,
  type IntakeService,
  detectChannels,
  loadClientChannels,
  generateIntakeLink,
  sendIntakeLinkViaWA,
  sendIntakeLinkViaEmail,
  logIntakeLinkSent,
  type ClientChannels,
} from '../lib/intakeLinkStore';

export default function IntakeLinkSendBar({
  cid,
  pid,
  propertyId,
  birthPersonId,
  birthPersonName,
  onClose,
}: {
  cid: string;
  /** Required for report-tier links (project-scoped). OPTIONAL for a birth_only
   *  link (Flag-#2, AD-008/AD-019): persons are client-scoped, so a birth_only
   *  link needs only cid + personId. Pass pid as context when available. */
  pid?: string;
  /** When set (e.g. opened from a Property page), the link is fixed to this
   *  property and the in-modal picker is suppressed. When omitted, staff scope
   *  the link via the embedded PropertyPicker (auto-selects single-property
   *  projects; shows a chooser for multi-property ones; '' = no scope). */
  propertyId?: string;
  /** M5-S5 — when set, the modal mints a `birth_only` link targeted at this
   *  person (DOB/TOB/POB only). The service selector + property picker are
   *  suppressed; the channel/send flow is identical. */
  birthPersonId?: string;
  /** Optional display name for the birth_only header. */
  birthPersonName?: string;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const { user } = useAuth();
  const isBirthOnly = !!birthPersonId;

  const [service, setService] = useState<IntakeService>('L1A');
  // Which property the link is scoped to. Seeded from the fixed prop; otherwise
  // resolved by PropertyPicker ('' = no property scope).
  const [selectedProperty, setSelectedProperty] = useState<string>(propertyId || '');
  const propertyFixed = !!propertyId;
  const [client, setClient] = useState<ClientChannels | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  // Generated link (regenerated when the service changes).
  const [linkId, setLinkId] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [genBusy, setGenBusy] = useState(false);

  // Channel toggles (seeded from detection; staff can override).
  const [waOn, setWaOn] = useState(false);
  const [emailOn, setEmailOn] = useState(false);
  const [sending, setSending] = useState(false);

  // 1. Load client channels on mount.
  useEffect(() => {
    let live = true;
    setLoading(true);
    loadClientChannels(cid)
      .then((c) => {
        if (!live) return;
        setClient(c);
        const det = detectChannels(c);
        // Seed toggles per AD-020 — every available channel on by default.
        setWaOn(det.hasWa);
        setEmailOn(det.hasEmail);
      })
      .catch((e) => {
        if (live) setLoadErr(e?.message || 'Could not load the client record.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [cid]);

  // 2. Generate (or regenerate) the link whenever the service changes and the
  //    client has at least one channel. Cheap; produces the canonical URL.
  useEffect(() => {
    if (loading || !client) return;
    const det = detectChannels(client);
    if (det.none) return; // no point minting a link we cannot send
    let live = true;
    setGenBusy(true);
    // M5-S5: a birth_only link targets a specific person (no property scope, no
    // report tier). Flag-#2 (AD-008/AD-019): pid is OPTIONAL for birth_only —
    // pass it only when present (as context), never block on its absence.
    // Otherwise (M5-S4) scope the link to the chosen property.
    const genArgs = isBirthOnly
      ? {
          cid,
          service: 'birth_only' as const,
          personId: birthPersonId,
          ...(pid ? { pid } : {}),
        }
      : { cid, pid, service, propertyId: selectedProperty || undefined };
    generateIntakeLink(genArgs)
      .then((r) => {
        if (!live) return;
        setLinkId(r.linkId);
        setUrl(r.url);
      })
      .catch((e) => {
        if (live) toast(e?.message || 'Could not generate the intake link.', { ok: false });
      })
      .finally(() => {
        if (live) setGenBusy(false);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, client, service, cid, pid, selectedProperty, isBirthOnly, birthPersonId]);

  const det = client ? detectChannels(client) : null;
  const noChannel = !!det && det.none;
  const canSend =
    !!linkId && !sending && !genBusy && ((waOn && det?.hasWa) || (emailOn && det?.hasEmail));

  async function onSend() {
    if (!linkId || !det) return;
    const tasks: Promise<unknown>[] = [];
    const labels: string[] = [];
    if (waOn && det.hasWa) {
      tasks.push(sendIntakeLinkViaWA(linkId));
      labels.push('WhatsApp');
    }
    if (emailOn && det.hasEmail) {
      tasks.push(sendIntakeLinkViaEmail(linkId));
      labels.push('email');
    }
    if (tasks.length === 0) return;

    setSending(true);
    const results = await Promise.allSettled(tasks);
    setSending(false);

    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const sentLabels = results
      .map((r, i) => (r.status === 'fulfilled' ? labels[i] : null))
      .filter(Boolean) as string[];
    const failed = results
      .map((r, i) => (r.status === 'rejected' ? labels[i] : null))
      .filter(Boolean) as string[];

    // STEP 3 (M5 Flag B fold-back): one internal project-thread note per send
    // ACTION, listing the channels that actually went out. Best-effort —
    // never blocks the (already-successful) send. PORTAL-side, NOT a CF.
    if (ok > 0 && pid) {
      void logIntakeLinkSent({
        pid,
        cid,
        clientName: client?.name || cid,
        channels: sentLabels,
        actor: { uid: user?.uid ?? null, name: 'Team' },
      }).catch(() => {
        /* non-fatal: the link was sent; the thread note is advisory */
      });
    }

    if (ok > 0 && failed.length === 0) {
      toast(`Intake link sent via ${labels.join(' + ')}.`, { ok: true });
      onClose();
    } else if (ok > 0) {
      toast(`Sent via ${labels.filter((l) => !failed.includes(l)).join(', ')}; ${failed.join(', ')} failed.`, { ok: false });
    } else {
      toast(`Could not send the intake link (${failed.join(', ')}). ${failed.includes('WhatsApp') ? 'The WhatsApp template may not be approved yet.' : ''}`.trim(), { ok: false });
    }
  }

  return (
    <div style={ovl} role="dialog" aria-modal="true" aria-label="Send intake form">
      <div style={sheet}>
        <div style={head}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--ink)' }}>
              {isBirthOnly ? 'Request birth details' : 'Send intake form'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-mute)', marginTop: 2 }}>
              {isBirthOnly
                ? `${birthPersonName || 'Person'} · ${client ? client.name : 'Loading client…'}`
                : `${client ? client.name : 'Loading client…'}`}
              {pid ? ` · ${pid}` : ''}
            </div>
          </div>
          <button style={xbtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div style={body}>
          {loading && (
            <div style={{ color: 'var(--ink-mute)', fontSize: 13 }}>Loading client record…</div>
          )}
          {loadErr && (
            <div style={errBox}>{loadErr}</div>
          )}

          {!loading && !loadErr && client && (
            <>
              {/* M5-S5: a birth_only request has no service/property choice —
                  it captures only this person's birth details. */}
              {isBirthOnly ? (
                <div style={{ marginBottom: 18, fontSize: 13, color: 'var(--ink-mute)', lineHeight: 1.55 }}>
                  This link asks <strong>{birthPersonName || 'this person'}</strong> for their date,
                  time and place of birth only — used for the astrology reading.
                </div>
              ) : (
                <>
                  {/* Service selector */}
                  <div style={{ marginBottom: 18 }}>
                    <label style={lbl}>Service</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {INTAKE_SERVICES.map((s) => (
                        <button
                          key={s}
                          onClick={() => setService(s)}
                          style={service === s ? svcPillOn : svcPill}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Property scope (M5-S4). Fixed when opened from a Property page;
                      otherwise the picker auto-selects single-property projects and
                      shows a chooser for multi-property ones (hidden when 0/1). */}
                  {propertyFixed ? (
                    <div style={{ marginBottom: 18 }}>
                      <label style={lbl}>Property</label>
                      <div style={linkBox}>{selectedProperty}</div>
                    </div>
                  ) : (
                    <div style={{ marginBottom: 18 }}>
                      {/* This branch only renders for report tiers, where pid is
                          always present; `pid || ''` narrows the now-optional type
                          (Flag-#2). '' → no property scope (PropertyPicker default). */}
                      <PropertyPicker
                        pid={pid || ''}
                        value={selectedProperty}
                        onSelect={setSelectedProperty}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Channel section */}
              <label style={lbl}>Send via</label>
              {noChannel ? (
                <div style={errBox}>
                  No contact channel available — add a WhatsApp number or email
                  to this client first.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {det?.hasWa && (
                    <button
                      onClick={() => setWaOn((v) => !v)}
                      style={waOn ? chipOn('wa') : chipOff}
                      aria-pressed={waOn}
                    >
                      <span style={dot('var(--accent)')} />
                      <span style={{ fontWeight: 600 }}>WhatsApp</span>
                      <span style={chipSub}>{client.whatsapp}</span>
                      <span style={chipState}>{waOn ? 'on' : 'off'}</span>
                    </button>
                  )}
                  {det?.hasEmail && (
                    <button
                      onClick={() => setEmailOn((v) => !v)}
                      style={emailOn ? chipOn('email') : chipOff}
                      aria-pressed={emailOn}
                    >
                      <span style={dot('var(--info)')} />
                      <span style={{ fontWeight: 600 }}>Email</span>
                      <span style={chipSub}>{client.email}</span>
                      <span style={chipState}>{emailOn ? 'on' : 'off'}</span>
                    </button>
                  )}
                </div>
              )}

              {/* Generated link preview */}
              {url && (
                <div style={{ marginTop: 16 }}>
                  <label style={lbl}>Link</label>
                  <div style={linkBox}>{url}</div>
                </div>
              )}
              {genBusy && (
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--ink-mute)' }}>
                  Generating link…
                </div>
              )}
            </>
          )}
        </div>

        <div style={foot}>
          <Btn3 intent="ghost" onClick={onClose}>
            Cancel
          </Btn3>
          <Btn3 intent="grn" onClick={onSend} disabled={!canSend}>
            {sending ? 'Sending…' : 'Confirm send'}
          </Btn3>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── styles (tokens only) ───────────────────────── */

const ovl: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(20,20,30,.42)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '6vh 16px',
  zIndex: 200,
  overflowY: 'auto',
};
const sheet: React.CSSProperties = {
  background: '#fff',
  borderRadius: 'var(--r-lg)',
  width: 'min(480px, 100%)',
  boxShadow: '0 24px 60px rgba(20,20,30,.4)',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '88vh',
};
const head: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid var(--rule)',
};
const xbtn: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--rule)',
  background: '#fff',
  cursor: 'pointer',
  color: 'var(--ink-soft)',
};
const body: React.CSSProperties = {
  padding: 20,
  overflowY: 'auto',
  flex: 1,
};
const foot: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 10,
  padding: '14px 20px',
  borderTop: '1px solid var(--rule)',
  background: 'var(--inset)',
};
const lbl: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ink-soft)',
  marginBottom: 8,
};
const errBox: React.CSSProperties = {
  border: '1px solid var(--crit)',
  background: 'var(--crit-wash, #FDECEC)',
  color: 'var(--crit)',
  borderRadius: 'var(--r-sm)',
  padding: '10px 12px',
  fontSize: 13,
  fontWeight: 600,
};
const linkBox: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 12,
  color: 'var(--ink-soft)',
  wordBreak: 'break-all',
  background: 'var(--inset)',
};
const svcPill: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  background: '#fff',
  color: 'var(--ink-soft)',
  borderRadius: 'var(--r-pill, 999px)',
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
const svcPillOn: React.CSSProperties = {
  ...svcPill,
  border: '1px solid var(--ink)',
  background: 'var(--ink)',
  color: '#fff',
};
const chipOff: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  border: '1px solid var(--rule-strong)',
  background: '#fff',
  borderRadius: 'var(--r-sm)',
  padding: '10px 12px',
  fontSize: 13.5,
  color: 'var(--ink-soft)',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
};
function chipOn(kind: 'wa' | 'email'): React.CSSProperties {
  return {
    ...chipOff,
    border: `1px solid ${kind === 'wa' ? 'var(--accent)' : 'var(--info)'}`,
    background: kind === 'wa' ? 'var(--accent-wash)' : 'var(--info-wash)',
    color: kind === 'wa' ? 'var(--accent-deep)' : 'var(--info)',
  };
}
function dot(color: string): React.CSSProperties {
  return {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
  };
}
const chipSub: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-mute)',
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const chipState: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
};
